import React, { useState, useEffect, useRef, useMemo } from "react";
import { api, getAuthHeaders } from "../utils/api";
import { useUser } from "../redux/useUser";
import { useDispatch, useSelector } from "react-redux";
import { updateUser } from "../redux/updateUserSlice";
import { useNavigate } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  Cell, AreaChart, Area, CartesianGrid, ScatterChart, Scatter, ZAxis,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ReferenceLine,
  Treemap,
} from "recharts";
import AssetDistributionChart from "../components/AssetDistributionChart";
import { TrendingUp, TrendingDown, Clock, ArrowUpRight, ArrowDownRight, Wallet, LayoutDashboard, AlertCircle, Activity, ChevronDown } from "lucide-react";
import Layout from "../components/Layout";
import Loader from "../components/Loader";
import CurrencyNotification from "../components/CurrencyNotification";
import MobileVerificationModal from "../components/MobileVerificationModal";
import "../styles/dashboard-2.css";
import InfoTooltip from "../components/InfoTooltip";
import { formatLargeNumber } from "../utils/formatNumber";

const NW_CHART_HEIGHT = 240;
const AVL_CHART_HEIGHT = 172;
const CHART_MARGIN = { top: 4, right: 8, left: 8, bottom: 0 };

// ─── Tick-Ring Gauge ─────────────────────────────────────────────────────────
const TickRingGauge = ({ percentage, size = 160, label = "change", dark = false }) => {
  const pct = Number.isFinite(Number(percentage)) ? Math.max(0, Math.min(100, Number(percentage))) : 0;
  const totalTicks = 72;
  const filledTicks = Math.round((pct / 100) * totalTicks);
  const cx = 100, cy = 100;
  const innerR = 68, outerR = 84;

  const ticks = Array.from({ length: totalTicks }, (_, i) => {
    const angle = (i / totalTicks) * 360 - 90;
    const rad = (angle * Math.PI) / 180;
    return {
      x1: cx + innerR * Math.cos(rad),
      y1: cy + innerR * Math.sin(rad),
      x2: cx + outerR * Math.cos(rad),
      y2: cy + outerR * Math.sin(rad),
      filled: i < filledTicks,
    };
  });

  return (
    <svg viewBox="0 0 200 200" width={size} height={size} style={{ display: "block" }}>
      {ticks.map((t, i) => (
        <line
          key={i}
          x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2}
          stroke={t.filled ? "#4f46e5" : (dark ? "rgba(255,255,255,0.12)" : "#eaedf5")}
          strokeWidth="3.5"
          strokeLinecap="round"
        />
      ))}
      <text x="100" y="96" textAnchor="middle" fontSize="34" fontWeight="800" fill="#fff" fontFamily="inherit">
        {Math.abs(pct).toFixed(0)}%
      </text>
      <text x="100" y="116" textAnchor="middle" fontSize="11" fontWeight="500" fill="rgba(255,255,255,0.5)" fontFamily="inherit">
        {label}
      </text>
    </svg>
  );
};

// ─── Dual-Ring Gauge (diversification — two concentric tick rings) ───────────
const DualRingGauge = ({ score = 0, size = 200 }) => {
  const pct = Math.max(0, Math.min(100, Number(score) || 0));
  const totalTicks = 60;
  const cx = 100, cy = 100;
  const targetFilled = Math.round((70 / 100) * totalTicks); // 70% = well-diversified threshold
  const scoreFilled  = Math.round((pct / 100) * totalTicks);

  const ticks = Array.from({ length: totalTicks }, (_, i) => {
    const angle = (i / totalTicks) * 360 - 90;
    const rad = (angle * Math.PI) / 180;
    const outerMid = 82, innerMid = 64, half = 6;
    return {
      i,
      ox1: cx + (outerMid - half) * Math.cos(rad), oy1: cy + (outerMid - half) * Math.sin(rad),
      ox2: cx + (outerMid + half) * Math.cos(rad), oy2: cy + (outerMid + half) * Math.sin(rad),
      ix1: cx + (innerMid - half) * Math.cos(rad), iy1: cy + (innerMid - half) * Math.sin(rad),
      ix2: cx + (innerMid + half) * Math.cos(rad), iy2: cy + (innerMid + half) * Math.sin(rad),
    };
  });

  return (
    <svg viewBox="0 0 200 200" width={size} height={size} style={{ display: "block" }}>
      {ticks.map(({ i, ox1, oy1, ox2, oy2, ix1, iy1, ix2, iy2 }) => (
        <g key={i}>
          <line x1={ox1} y1={oy1} x2={ox2} y2={oy2}
            stroke={i < targetFilled ? "#0f1020" : "#eaedf5"}
            strokeWidth="3.5" strokeLinecap="round" />
          <line x1={ix1} y1={iy1} x2={ix2} y2={iy2}
            stroke={i < scoreFilled ? "#4f46e5" : "#eaedf5"}
            strokeWidth="3.5" strokeLinecap="round" />
        </g>
      ))}
      <text x="100" y="95" textAnchor="middle" fontSize="30" fontWeight="800" fill="#0f1020" fontFamily="inherit">
        {pct}%
      </text>
      <text x="100" y="113" textAnchor="middle" fontSize="10" fontWeight="600" fill="#9898b0" fontFamily="inherit">
        score
      </text>
    </svg>
  );
};

// ─── Half Circle Gauge (diversification) ─────────────────────────────────────
const HalfGauge = ({ percentage }) => {
  const pct = Number.isFinite(Number(percentage)) ? Math.max(0, Math.min(100, Number(percentage))) : 0;
  const r = 54;
  const circumference = Math.PI * r;
  const offset = circumference - (pct / 100) * circumference;

  return (
    <svg viewBox="0 0 140 80" width="140" height="80">
      <defs>
        <linearGradient id="hgGrad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#6366f1" />
          <stop offset="100%" stopColor="#a855f7" />
        </linearGradient>
      </defs>
      <path d={`M ${70 - r} 70 A ${r} ${r} 0 0 1 ${70 + r} 70`}
        fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="10" />
      <path d={`M ${70 - r} 70 A ${r} ${r} 0 0 1 ${70 + r} 70`}
        fill="none" stroke="url(#hgGrad)" strokeWidth="10"
        strokeDasharray={circumference} strokeDashoffset={offset}
        strokeLinecap="round" />
      <text x="70" y="58" textAnchor="middle" fontSize="20" fontWeight="800" fill="#fff" fontFamily="inherit">
        {pct}%
      </text>
    </svg>
  );
};

// ─── Period Tabs ──────────────────────────────────────────────────────────────
const PeriodTabs = ({ value, onChange, options }) => (
  <div className="db2-period-tabs">
    {options.map((o) => (
      <button
        key={o.value}
        className={`db2-period-tab${value === o.value ? " active" : ""}`}
        onClick={() => onChange(o)}
      >
        {o.label}
      </button>
    ))}
  </div>
);

// ─── Trend Pill ───────────────────────────────────────────────────────────────
const TrendPill = ({ value }) => {
  const num = Number(value);
  if (!Number.isFinite(num) || num === 0)
    return <span className="db2-trend-pill flat">—</span>;
  return (
    <span className={`db2-trend-pill ${num > 0 ? "up" : "down"}`}>
      {num > 0 ? <ArrowUpRight size={11} /> : <ArrowDownRight size={11} />}
      {Math.abs(num).toFixed(1)}%
    </span>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const Dashboard2 = () => {
  const navigate = useNavigate();
  const [barHover, setBarHover] = useState(null);
  const [darkFilter, setDarkFilter] = useState("Assets");

  const weekDays = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
  const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  // ── Asset vs Liability ─────────────────────────────────────────────────────
  const [avlData, setAvlData] = useState([]);
  const [avlPeriod, setAvlPeriod] = useState("monthly");

  useEffect(() => {
    api.get(`/dashboard/charts/asset-liability-breakdown?period=${avlPeriod}`, { headers: getAuthHeaders() })
      .then((res) => {
        const raw = Array.isArray((res.data || res).data) ? (res.data || res).data : [];
        const vals = raw.map((i) => Number(i.assets || 0)).filter((v) => v > 0 && v < 1e9);
        const median = vals.length ? vals.sort((a,b)=>a-b)[Math.floor(vals.length/2)] : 1e5;
        const sf = median < 1e9 ? 1 : 1e9 / median / 10;
        setAvlData(raw.slice(-30).map((item, idx) => {
          let a = Number(item.assets||0), l = Number(item.liabilities||item.liability||0);
          if (a > 1e9 || l > 1e9) { a/=sf; l/=sf; }
          return { ...item, day: idx+1, assets: Math.round(a), liabilities: Math.round(l) };
        }));
      }).catch(() => {});
  }, [avlPeriod]);

  // ── Net Worth chart ────────────────────────────────────────────────────────
  const [nwPeriod, setNwPeriod] = useState("monthly");
  const [nwChart, setNwChart] = useState([]);

  // ── Pill Bar chart (independent state, always monthly/yearly) ──────────────
  const [pillPeriod, setPillPeriod] = useState("monthly");
  const [pillNwChart, setPillNwChart] = useState([]);

  useEffect(() => {
    api.get(`/dashboard/charts/networth?period=${pillPeriod}`, { headers: getAuthHeaders() })
      .then((res) => {
        setPillNwChart(Array.isArray((res.data||res).data) ? (res.data||res).data : (res.data||res).chart || []);
      }).catch(() => {});
  }, [pillPeriod]);

  // ── Year-in-Review (always monthly, independent) ───────────────────────────
  const [yearNwChart, setYearNwChart] = useState([]);
  useEffect(() => {
    api.get("/dashboard/charts/networth?period=monthly", { headers: getAuthHeaders() })
      .then((res) => {
        setYearNwChart(Array.isArray((res.data||res).data) ? (res.data||res).data : (res.data||res).chart || []);
      }).catch(() => {});
  }, []);

  useEffect(() => {
    api.get(`/dashboard/charts/networth?period=${nwPeriod}`, { headers: getAuthHeaders() })
      .then((res) => {
        setNwChart(Array.isArray((res.data||res).data) ? (res.data||res).data : (res.data||res).chart || []);
      }).catch(() => {});
  }, [nwPeriod]);

  const chartData = useMemo(() => {
    if (!Array.isArray(nwChart)) return [];
    if (nwPeriod === "weekly") {
      const map = {};
      nwChart.forEach((item) => {
        if (!item?.date) return;
        const d = new Date(item.date);
        const idx = d.getUTCDay() === 0 ? 6 : d.getUTCDay() - 1;
        if (!map[idx]) map[idx] = { day: weekDays[idx], value: 0 };
        map[idx].value += Number(item.value||0);
      });
      return weekDays.map((name, i) => map[i] || { day: name, value: 0 });
    }
    if (nwPeriod === "monthly") {
      const map = {};
      nwChart.forEach((item) => {
        if (!item?.date) return;
        const mi = new Date(item.date).getMonth();
        if (!map[mi]) map[mi] = { day: monthNames[mi], value: 0 };
        map[mi].value += Number(item.value||0);
      });
      return monthNames.map((name, i) => map[i] || { day: name, value: 0 });
    }
    if (nwPeriod === "yearly") {
      const cy = new Date().getFullYear();
      const years = Array.from({length:6}, (_,i) => cy-5+i);
      const map = {};
      nwChart.forEach((item) => {
        if (!item?.date) return;
        const yr = new Date(item.date).getFullYear();
        if (!map[yr]) map[yr] = { year: String(yr), value: 0 };
        map[yr].value += Number(item.value||0);
      });
      return years.map((yr) => map[yr] || { year: String(yr), value: 0 });
    }
    // daily
    const map = {};
    const today = new Date();
    nwChart.forEach((item) => {
      if (!item?.date) return;
      const d = new Date(item.date);
      const key = `${monthNames[d.getMonth()]} ${String(d.getDate()).padStart(2,"0")}`;
      if (!map[key]) map[key] = { day: key, value: 0 };
      map[key].value += Number(item.value||0);
    });
    return Array.from({length:14}, (_,i) => {
      const d = new Date(today); d.setDate(today.getDate()-13+i);
      const key = `${monthNames[d.getMonth()]} ${String(d.getDate()).padStart(2,"0")}`;
      return map[key] || { day: key, value: 0 };
    });
  }, [nwChart, nwPeriod]);

  // lineChartsData for AVL
  const lineChartsData = useMemo(() => {
    const raw = avlData.map((i) => ({
      ...i, assets: Number(i.assets||0), liabilities: Number(i.liabilities||i.liability||0),
    }));
    const weekly = weekDays.map((day, idx) => {
      const item = raw.find((r) => {
        const label = (r.label||"").split(",")[0]?.trim();
        return label === day;
      });
      return item ? { day, assets: item.assets, liabilities: item.liabilities } : { day, assets: 0, liabilities: 0 };
    });
    const cy = new Date().getFullYear();
    const mMap = {};
    raw.forEach((item) => {
      if (!item.date) return;
      const d = new Date(item.date);
      if (d.getFullYear() !== cy) return;
      const mi = d.getUTCMonth();
      if (!mMap[mi]) mMap[mi] = { month: monthNames[mi], assets: 0, liabilities: 0 };
      mMap[mi].assets += item.assets;
      mMap[mi].liabilities += item.liabilities;
    });
    const monthly = monthNames.map((name, i) => mMap[i] || { month: name, assets: 0, liabilities: 0 });
    const years = Array.from({length:5}, (_,i) => cy-4+i);
    const yMap = {};
    years.forEach((yr) => { yMap[yr] = { year: String(yr), assets: 0, liabilities: 0 }; });
    raw.forEach((item) => {
      if (!item.date) return;
      const yr = new Date(item.date).getFullYear();
      if (yMap[yr]) { yMap[yr].assets += item.assets; yMap[yr].liabilities += item.liabilities; }
    });
    const yearly = years.map((yr) => yMap[yr]);
    return { weekly, monthly, yearly };
  }, [avlData]);

  const avlChartData = avlPeriod === "weekly" ? lineChartsData.weekly
    : avlPeriod === "yearly" ? lineChartsData.yearly
    : lineChartsData.monthly;
  const avlXKey = avlPeriod === "yearly" ? "year" : avlPeriod === "monthly" ? "month" : "day";

  const liabTrend = useMemo(() => {
    if (!avlChartData || avlChartData.length < 2) return null;
    const first = avlChartData[0]?.liabilities;
    const last = avlChartData[avlChartData.length - 1]?.liabilities;
    if (!first || first === 0) return null;
    return ((last - first) / first) * 100;
  }, [avlChartData]);

  // ── Top / Under performing ─────────────────────────────────────────────────
  const [topAssets, setTopAssets] = useState([]);
  const [underAssets, setUnderAssets] = useState([]);

  useEffect(() => {
    api.get("/assets/analytics/top-performing?limit=3", { headers: getAuthHeaders() })
      .then((res) => { const d = res.data||res; setTopAssets(Array.isArray(d)?d:d.assets||[]); }).catch(()=>{});
    api.get("/assets/analytics/underperforming?limit=3", { headers: getAuthHeaders() })
      .then((res) => { const d = res.data||res; setUnderAssets(Array.isArray(d)?d:d.assets||[]); }).catch(()=>{});
  }, []);

  // ── Diversification ────────────────────────────────────────────────────────
  const [divers, setDivers] = useState(null);
  useEffect(() => {
    api.get("/dashboard/assets/diversification", { headers: getAuthHeaders() })
      .then((res) => setDivers(res.data||res)).catch(()=>{});
  }, []);

  // ── Dashboard Summary ──────────────────────────────────────────────────────
  const [summary, setSummary] = useState({
    netWorth: null, totalAssets: null, totalLiabilities: null,
    lastUpdated: null, currency: null, netWorthChangePercentage: null,
  });
  const [summaryLoading, setSummaryLoading] = useState(false);

  useEffect(() => {
    setSummaryLoading(true);
    Promise.all([
      api.get("/dashboard/summary", { headers: getAuthHeaders() }),
      api.get("/liabilities?limit=1", { headers: getAuthHeaders() }),
    ]).then(([sRes, lRes]) => {
      const data = sRes.data || sRes;
      const lCount = lRes.data?.totalResults || lRes.data?.results?.length || 0;
      setSummary({
        netWorth: data.netWorth,
        totalAssets: data.totalAssets,
        totalLiabilities: { ...data.totalLiabilities, count: lCount },
        lastUpdated: data.lastUpdated,
        currency: data.currency,
        netWorthChangePercentage: data.netWorthChangePercentage,
      });
      setSummaryLoading(false);
    }).catch(() => setSummaryLoading(false));
  }, []);

  // ── User / phone / currency (unchanged logic) ──────────────────────────────
  const { user, loading, getUser } = useUser();
  const allowedCurrencies = ["NZD","AUD","USD","INR","EUR"];
  const autoCurrencyUpdateRef = useRef(false);
  const updating = useSelector((s) => s.updateUser?.updating);
  const dispatch = useDispatch();
  const didFetchMeRef = useRef(false);

  useEffect(() => {
    if (didFetchMeRef.current) return;
    didFetchMeRef.current = true;
    const token = localStorage.getItem("access_token");
    if (!token) return;
    if (typeof getUser === "function") { try { getUser(token).catch(()=>{}); } catch(e){} return; }
    api.get("/me", { headers: getAuthHeaders() }).catch((err) => {
      if (err?.response?.status === 401) localStorage.removeItem("access_token");
    });
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    const existing = user.defaultCurrency || user.currency || "";
    if (existing.trim()) return;
    if (autoCurrencyUpdateRef.current || updating) return;
    autoCurrencyUpdateRef.current = true;
    (async () => {
      try {
        const resp = await fetch("https://ipapi.co/json/");
        const data = await resp.json();
        const detected = data?.currency ? String(data.currency).toUpperCase() : "";
        const toSet = allowedCurrencies.includes(detected) ? detected : "NZD";
        if (toSet) await dispatch(updateUser({ id: user.id||user._id, data: { currency: toSet, region: toSet } }));
      } catch(e) { console.warn("Auto currency detection failed", e); }
      finally { setTimeout(() => { autoCurrencyUpdateRef.current = false; }, 500); }
    })();
  }, [user, dispatch, updating]);

  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [hasPhoneLinkError, setHasPhoneLinkError] = useState(false);
  const [isPhoneUnlinked, setIsPhoneUnlinked] = useState(false);
  // UX-03: same session dismissal as Dashboard.js (only while the modal is closable)
  const phoneModalDismissKey = user?.id ? `phone_modal_dismissed_${user.id}` : null;
  const [phoneModalDismissed, setPhoneModalDismissed] = useState(false);
  useEffect(() => {
    if (!phoneModalDismissKey) return;
    try { setPhoneModalDismissed(sessionStorage.getItem(phoneModalDismissKey) === "true"); } catch (e) { setPhoneModalDismissed(false); }
  }, [phoneModalDismissKey]);
  const GRACE = 15 * 60 * 1000;
  const [showCurrencyFlash, setShowCurrencyFlash] = useState(false);
  const currencyTimer = useRef(null);

  const triggerCurrencyFlash = () => {
    const cur = localStorage.getItem("detectedCurrency") || localStorage.getItem("user_currency");
    if (!cur || localStorage.getItem("hasSeenCurrencyNotification") === "true") return;
    setShowCurrencyFlash(true);
    localStorage.setItem("hasSeenCurrencyNotification", "true");
    localStorage.removeItem("detectedCurrency");
    currencyTimer.current = setTimeout(() => setShowCurrencyFlash(false), 5000);
  };

  useEffect(() => {
    if (localStorage.getItem("phone_link_error")) setHasPhoneLinkError(true);
    const had409 = localStorage.getItem("phone_link_error_persistent");
    if (had409 === "true") {
      const t = parseInt(localStorage.getItem("phone_link_error_persistent_time")||"0",10);
      if (t && Date.now()-t < GRACE) setHasPhoneLinkError(true);
      else { localStorage.removeItem("phone_link_error_persistent"); localStorage.removeItem("phone_link_error_persistent_time"); }
    }
  }, []);

  useEffect(() => {
    if (loading || !user?.id) return;
    const identities = Array.isArray(user?.identities) ? user.identities : [];
    const hasPhone = identities.some((i) => i.provider === "sms");
    if (hasPhone) { setPhoneVerified(true); setShowPhoneModal(false); localStorage.removeItem("phone_verified"); return; }
    const wasPrev = localStorage.getItem("phone_verified") === "true";
    const wasLinked = localStorage.getItem("phone_was_linked") === "true";
    setIsPhoneUnlinked((wasPrev || wasLinked) && !hasPhone);
    if (!localStorage.getItem("phone_modal_first_shown")) localStorage.setItem("phone_modal_first_shown", Date.now().toString());
    setShowPhoneModal(true);
    setPhoneVerified(false);
  }, [user, loading]);

  const handleLogout = () => {
    ["access_token","user","user_info","id_token","hideProfileBanner","profileCompletionPercent",
     "pending_verification_email","user_profile","update_phone","phone","smsIdentity","update_phone_userId"]
      .forEach((k) => localStorage.removeItem(k));
    navigate("/");
  };

  const handlePhoneVerified = async (phoneNumber) => {
    localStorage.setItem("phone_verified","true");
    localStorage.setItem("phone_was_linked","true");
    localStorage.removeItem("phone_link_error_persistent");
    setPhoneVerified(true); setShowPhoneModal(false); setIsPhoneUnlinked(false); setHasPhoneLinkError(false);
    const token = localStorage.getItem("access_token");
    if (getUser) getUser(token);
  };

  useEffect(() => {
    const st = localStorage.getItem("phone_verified_success_time");
    if (st && Date.now()-parseInt(st,10)<5000) {
      localStorage.setItem("phone_verified","true");
      setPhoneVerified(true); setShowPhoneModal(false); setIsPhoneUnlinked(false);
      localStorage.removeItem("phone_link_error_persistent"); localStorage.removeItem("phone_link_error_persistent_time");
    }
    localStorage.removeItem("phone_verified_success"); localStorage.removeItem("phone_verified_success_time");
    const et = localStorage.getItem("phone_link_error_time");
    const em = localStorage.getItem("phone_link_error");
    if (et && em) {
      localStorage.setItem("phone_link_error_persistent","true"); localStorage.setItem("phone_link_error_persistent_time",et);
      setHasPhoneLinkError(true); localStorage.removeItem("phone_link_error_time");
    }
    const h409 = localStorage.getItem("phone_link_error_persistent");
    if (h409 === "true") {
      const t = parseInt(localStorage.getItem("phone_link_error_persistent_time")||"0",10);
      if (t && Date.now()-t<GRACE) setHasPhoneLinkError(true);
      else { localStorage.removeItem("phone_link_error_persistent"); localStorage.removeItem("phone_link_error_persistent_time"); }
    }
  }, []);

  const isModalClosable = useMemo(() => {
    if (hasPhoneLinkError) return true;
    const ca = user?.createdAt;
    if (!ca) return true;
    const days = Math.ceil((new Date()-new Date(ca))/(1000*60*60*24));
    if (isPhoneUnlinked && days >= 7) return false;
    return days < 7;
  }, [user, hasPhoneLinkError, isPhoneUnlinked]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  function formatLastUpdated(d) {
    if (!d) return "Today";
    const diff = Math.floor((new Date()-new Date(d))/(1000*60));
    if (diff < 1) return "Just now";
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff/60)}h ago`;
    return `${Math.floor(diff/1440)}d ago`;
  }

  const cur = summary.currency || "";

  const absoluteNWChange = useMemo(() => {
    if (summary.netWorth == null || !summary.netWorthChangePercentage) return null;
    const p = Number(summary.netWorthChangePercentage);
    if (!Number.isFinite(p) || p === 0) return null;
    return summary.netWorth * (p / 100) / (1 + p / 100);
  }, [summary.netWorth, summary.netWorthChangePercentage]);

  const debtRatio = useMemo(() => {
    const assets = summary.totalAssets?.value;
    const liab = summary.totalLiabilities?.value;
    if (!assets || assets === 0 || liab == null || liab === 0) return null;
    return Math.round((liab / assets) * 100);
  }, [summary]);

  const debtHealth = debtRatio === null ? null
    : debtRatio < 30 ? { label: "Healthy", cls: "kpi-green", iconCls: "green" }
    : debtRatio < 50 ? { label: "Moderate", cls: "kpi-orange", iconCls: "orange" }
    : { label: "High Risk", cls: "kpi-red", iconCls: "red" };

  const yearProgress = useMemo(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 1);
    const end = new Date(now.getFullYear() + 1, 0, 1);
    return Math.round(((now - start) / (end - start)) * 100);
  }, []);

  const periodStartVal = useMemo(() => {
    const nonZero = chartData.find(d => d.value > 0);
    return nonZero?.value ?? 0;
  }, [chartData]);

  // Index and % change for the highlighted bar in NW hero chart
  const nwActiveBarIdx = useMemo(() => {
    if (!chartData.length) return 0;
    return chartData.reduce((best, d, i) => (d.value || 0) > (chartData[best]?.value || 0) ? i : best, 0);
  }, [chartData]);

  const nwActivePct = useMemo(() => {
    if (nwActiveBarIdx < 1 || !chartData.length) return null;
    const curr = chartData[nwActiveBarIdx]?.value || 0;
    const prev = chartData[nwActiveBarIdx - 1]?.value || 0;
    if (!prev) return null;
    return ((curr - prev) / prev) * 100;
  }, [chartData, nwActiveBarIdx]);

  const pillChartData = useMemo(() => {
    if (!Array.isArray(pillNwChart)) return [];
    if (pillPeriod === "monthly") {
      const map = {};
      pillNwChart.forEach((item) => {
        if (!item?.date) return;
        const mi = new Date(item.date).getMonth();
        if (!map[mi]) map[mi] = { day: monthNames[mi], value: 0 };
        map[mi].value += Number(item.value || 0);
      });
      return monthNames.map((name, i) => map[i] || { day: name, value: 0 });
    }
    const cy = new Date().getFullYear();
    const years = Array.from({ length: 6 }, (_, i) => cy - 5 + i);
    const map = {};
    pillNwChart.forEach((item) => {
      if (!item?.date) return;
      const yr = new Date(item.date).getFullYear();
      if (!map[yr]) map[yr] = { year: String(yr), value: 0 };
      map[yr].value += Number(item.value || 0);
    });
    return years.map((yr) => map[yr] || { year: String(yr), value: 0 });
  }, [pillNwChart, pillPeriod]);

  const pillActiveBarIdx = useMemo(() => {
    if (!pillChartData.length) return 0;
    return pillChartData.reduce((best, d, i) => (d.value || 0) > (pillChartData[best]?.value || 0) ? i : best, 0);
  }, [pillChartData]);

  const pillActivePct = useMemo(() => {
    if (pillActiveBarIdx < 1 || !pillChartData.length) return null;
    const curr = pillChartData[pillActiveBarIdx]?.value || 0;
    const prev = pillChartData[pillActiveBarIdx - 1]?.value || 0;
    if (!prev) return null;
    return ((curr - prev) / prev) * 100;
  }, [pillChartData, pillActiveBarIdx]);

  const yearReviewData = useMemo(() => {
    const map = {};
    yearNwChart.forEach((item) => {
      if (!item?.date) return;
      const mi = new Date(item.date).getMonth();
      if (!map[mi]) map[mi] = { day: monthNames[mi], value: 0 };
      map[mi].value += Number(item.value || 0);
    });
    return monthNames.map((name, i) => map[i] || { day: name, value: 0 });
  }, [yearNwChart]);

  const diversScore = divers?.score ?? null;

  const radarData = useMemo(() => {
    const nwVal = (summary.netWorth || 0) > 0 ? Math.min(100, Math.log10(Math.max(1, summary.netWorth)) * 10) : 0;
    const divScore = diversScore ?? 0;
    const debtScore = debtRatio !== null ? Math.max(0, 100 - debtRatio * 2) : 50;
    const avgRet = topAssets.length
      ? topAssets.reduce((s, a) => s + +(a.performanceMetrics?.totalReturnPercentage ?? 0), 0) / topAssets.length
      : 0;
    const perfScore = Math.min(100, Math.max(0, 50 + avgRet));
    const coverScore = Math.min(100, (summary.totalAssets?.count || 0) * 12);
    return [
      { axis: "Net Worth",   value: Math.round(nwVal) },
      { axis: "Diversified", value: Math.round(divScore) },
      { axis: "Debt Health", value: Math.round(debtScore) },
      { axis: "Performance", value: Math.round(perfScore) },
      { axis: "Coverage",    value: Math.round(coverScore) },
    ];
  }, [summary, diversScore, debtRatio, topAssets]);

  const bubblePackData = useMemo(() => {
    const allA = [...topAssets, ...underAssets];
    if (!allA.length) return [];
    const maxVal = Math.max(...allA.map(a => a.currentValue || a.value || 0), 1);
    const items = allA.map((a) => ({
      name: (a.name || "Asset").slice(0, 14),
      value: a.currentValue || a.value || 0,
      ret: +(a.performanceMetrics?.totalReturnPercentage ?? 0),
      r: 26 + Math.sqrt((a.currentValue || a.value || 0) / maxVal) * 54,
    }));
    const W = 420, H = 260;
    const sorted = [...items].sort((b1, b2) => b2.r - b1.r);
    const placed = [];
    for (const item of sorted) {
      let pos = { x: W / 2, y: H / 2 };
      for (let t = 0; t < 3000; t++) {
        const angle = t * 2.3999;
        const rad = 1.4 * Math.sqrt(t);
        const cx = W / 2 + rad * Math.cos(angle);
        const cy = H / 2 + rad * Math.sin(angle);
        if (cx - item.r < 6 || cx + item.r > W - 6 || cy - item.r < 6 || cy + item.r > H - 6) continue;
        if (placed.every(p => Math.hypot(cx - p.x, cy - p.y) >= item.r + p.r + 4)) {
          pos = { x: cx, y: cy }; break;
        }
      }
      placed.push({ ...item, x: pos.x, y: pos.y });
    }
    return placed;
  }, [topAssets, underAssets]);

  const sunburstData = useMemo(() => {
    const allA = [...topAssets, ...underAssets];
    if (!allA.length) return null;
    const catMap = {};
    allA.forEach(a => {
      const cat = (a.assetType || a.category || "Other").slice(0, 14);
      const val = a.currentValue || a.value || 0;
      if (!catMap[cat]) catMap[cat] = { name: cat, value: 0, assets: [] };
      catMap[cat].value += val;
      catMap[cat].assets.push({ name: (a.name || "").slice(0, 12), value: val });
    });
    return Object.values(catMap);
  }, [topAssets, underAssets]);

  const BarTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const v = Number(payload[0]?.value ?? payload[0]?.payload?.value);
    if (!Number.isFinite(v)) return null;
    return <div className="db2-tooltip">{cur} {formatLargeNumber(v, cur)}</div>;
  };

  const AreaTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="db2-tooltip">
        <p className="db2-tooltip-label">{label}</p>
        {payload.map((entry) => (
          <div key={entry.dataKey} className="db2-tooltip-row">
            <span className="db2-tooltip-dot" style={{ background: entry.stroke }} />
            <span style={{ marginRight: 4 }}>{entry.name}:</span>
            <strong>{cur} {formatLargeNumber(entry.value, cur)}</strong>
          </div>
        ))}
      </div>
    );
  };

  const ScatterTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    return (
      <div className="db2-tooltip">
        <p style={{ fontWeight: 700, marginBottom: 4 }}>{d.name}</p>
        <div>{cur} {formatLargeNumber(d.value, cur)}</div>
        <div style={{ color: d.ret >= 0 ? "#34d399" : "#f87171" }}>
          {d.ret >= 0 ? "+" : ""}{d.ret.toFixed(1)}% return
        </div>
      </div>
    );
  };

  const diversLabel = diversScore === null ? "No data yet"
    : diversScore >= 70 ? "Well diversified"
    : diversScore >= 40 ? "Moderately diversified"
    : "Needs diversification";

  const nwXKey = nwPeriod === "yearly" ? "year" : "day";

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <MobileVerificationModal
        open={showPhoneModal && !phoneVerified && !(phoneModalDismissed && isModalClosable)}
        onClose={() => {
          if (isModalClosable) {
            setShowPhoneModal(false);
            setPhoneModalDismissed(true);
            try { if (phoneModalDismissKey) sessionStorage.setItem(phoneModalDismissKey, "true"); } catch (e) { /* prompt returns next visit */ }
            triggerCurrencyFlash();
          }
        }}
        onVerified={(r) => { handlePhoneVerified(r); triggerCurrencyFlash(); }}
        onLogout={handleLogout}
        isClosable={isModalClosable}
      />
      {loading && <Loader />}
      <CurrencyNotification />

      {showCurrencyFlash && (() => {
        const currency = localStorage.getItem("user_currency") || "INR";
        const { getCurrencyDisplayName } = require("../utils/currencyDetection");
        return (
          <div style={{ position:"fixed",top:0,left:0,right:0,background:"linear-gradient(90deg,#2929e5,#8b5cf6)",color:"#fff",textAlign:"center",padding:"14px 48px 14px 16px",zIndex:999999,fontSize:15,fontWeight:500 }}>
            Currency set to <strong>{currency} ({getCurrencyDisplayName(currency)})</strong>.{" "}
            <span onClick={() => { setShowCurrencyFlash(false); navigate("/editprofile"); }} style={{ textDecoration:"underline",cursor:"pointer",fontWeight:700 }}>Edit Profile</span>
            <button onClick={() => setShowCurrencyFlash(false)} style={{ position:"absolute",right:16,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",color:"#fff",fontSize:20,cursor:"pointer",lineHeight:1 }}>×</button>
          </div>
        );
      })()}

      <Layout>
        <div className="db2-page">

          {/* ── Page Header ── */}
          <div className="db2-page-header">
            <div>
              <div className="db2-greeting">
                {(() => { const h = new Date().getHours(); return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening"; })()}
                {user?.name ? `, ${user.name.split(" ")[0]}!` : "!"}
              </div>
              <div className="db2-greeting-sub">
                {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
              </div>
            </div>
            <div className="db2-header-badge">
              <span className="db2-header-badge-dot" />
              Portfolio Live
            </div>
            {/* Big header stats — like "56 Total Clients / 247 Projects Completed" */}
            {summary.totalAssets?.value != null && (() => {
              const assetsVal = summary.totalAssets.value || 0;
              const liabVal = summary.totalLiabilities?.value ?? 0;
              const total = assetsVal + liabVal;
              const assetPct = total > 0 ? (assetsVal / total) * 100 : 100;
              return (
                <div className="db2-header-stats">
                  <div className="db2-header-stat db2-header-stat--assets">
                    <span className="db2-header-stat-icon"><ArrowUpRight size={16} strokeWidth={2.5} /></span>
                    <div className="db2-header-stat-body">
                      <span className="db2-header-stat-label">Total Assets</span>
                      <span className="db2-header-stat-num">
                        {formatLargeNumber(assetsVal, cur)}
                        <span className="db2-header-stat-cur">{cur}</span>
                      </span>
                    </div>
                  </div>
                  <div className="db2-header-stat db2-header-stat--liab">
                    <span className="db2-header-stat-icon"><ArrowDownRight size={16} strokeWidth={2.5} /></span>
                    <div className="db2-header-stat-body">
                      <span className="db2-header-stat-label">Liabilities</span>
                      <span className="db2-header-stat-num">
                        {formatLargeNumber(liabVal, cur)}
                        <span className="db2-header-stat-cur">{cur}</span>
                      </span>
                    </div>
                  </div>
                  <div className="db2-header-ratio" title={`Assets ${assetPct.toFixed(1)}% · Liabilities ${(100 - assetPct).toFixed(1)}%`}>
                    <span className="db2-header-ratio-assets" style={{ width: `${assetPct}%` }} />
                  </div>
                </div>
              );
            })()}
          </div>

          <div className="db2-bento">

            {/* ══ ROW 1: KPI Stat cards with mini sparklines ══ */}

            {/* Net Worth KPI */}
            <div className="db2-kpi-stat">
              <div className="db2-kpi-stat-left">
                <div className="db2-kpi-stat-icon"><LayoutDashboard size={18} /></div>
                <div className="db2-kpi-stat-info">
                  <div className="db2-kpi-stat-label">Net Worth <InfoTooltip content="Total Assets minus Total Liabilities." /></div>
                  <div className="db2-kpi-stat-row">
                    <span className="db2-kpi-stat-num">
                      <span className="db2-kpi-stat-cur">{cur} </span>
                      {summary.netWorth !== null ? formatLargeNumber(summary.netWorth, cur) : summaryLoading ? "…" : "0"}
                    </span>
                    <TrendPill value={summary.netWorthChangePercentage} />
                  </div>
                  {absoluteNWChange != null && (
                    <span className={`db2-abs-change ${Number(summary.netWorthChangePercentage) >= 0 ? "pos" : "neg"}`}>
                      {Number(summary.netWorthChangePercentage) >= 0 ? "+" : "−"}{cur} {formatLargeNumber(Math.abs(absoluteNWChange), cur)}
                    </span>
                  )}
                </div>
              </div>
              <div className="db2-kpi-mini-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData.slice(-6)} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="miniNw" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#4f46e5" stopOpacity="0.22" />
                        <stop offset="100%" stopColor="#4f46e5" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    <Area type="monotone" dataKey="value" stroke="#4f46e5" strokeWidth={2} fill="url(#miniNw)" dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Total Assets KPI */}
            <div className="db2-kpi-stat">
              <div className="db2-kpi-stat-left">
                <div className="db2-kpi-stat-icon"><Wallet size={18} /></div>
                <div className="db2-kpi-stat-info">
                  <div className="db2-kpi-stat-label">Total Assets</div>
                  <div className="db2-kpi-stat-row">
                    <span className="db2-kpi-stat-num">
                      <span className="db2-kpi-stat-cur">{cur} </span>
                      {summary.totalAssets?.value !== undefined ? formatLargeNumber(summary.totalAssets.value, cur) : "0"}
                    </span>
                  </div>
                  <span className="db2-kpi-stat-sub">{summary.totalAssets?.count || 0} assets tracked</span>
                </div>
              </div>
              <div className="db2-kpi-mini-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData.slice(-6)} margin={{ top: 4, right: 0, left: 0, bottom: 0 }} barCategoryGap="25%">
                    <Bar dataKey="value" radius={[3, 3, 0, 0]}>
                      {chartData.slice(-6).map((_, i, arr) => (
                        <Cell key={i} fill={i === arr.length - 1 ? "#4f46e5" : "#c7d2fe"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Debt Ratio KPI */}
            <div className="db2-kpi-stat">
              <div className="db2-kpi-stat-left">
                <div className="db2-kpi-stat-icon"><Activity size={18} /></div>
                <div className="db2-kpi-stat-info">
                  <div className="db2-kpi-stat-label">Debt Ratio <InfoTooltip content="Liabilities as % of assets. Under 30% is healthy." /></div>
                  <div className="db2-kpi-stat-row">
                    <span className="db2-kpi-stat-num">{debtRatio !== null ? `${debtRatio}%` : summaryLoading ? "…" : "—"}</span>
                    {debtHealth && <span className={`db2-health-badge ${debtHealth.iconCls}`}>{debtHealth.label}</span>}
                  </div>
                  <span className="db2-kpi-stat-sub">{formatLastUpdated(summary.lastUpdated)}</span>
                </div>
              </div>
              <div className="db2-kpi-mini-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={avlChartData.slice(-6)} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="miniLiab" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#f97316" stopOpacity="0.2" />
                        <stop offset="100%" stopColor="#f97316" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    <Area type="monotone" dataKey="liabilities" stroke="#f97316" strokeWidth={2} fill="url(#miniLiab)" dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* ══ ROW 2: Net Worth Hero Chart (full width, dark navy) ══ */}
            <div className="db2-card db2-card-dark db2-span-3 db2-nw-hero">
              <div className="db2-nw-hero-header">
                <div className="db2-nw-hero-left">
                  <div className="db2-card-label" style={{ marginBottom: 2 }}>
                    <LayoutDashboard size={11} /> Portfolio Trend
                  </div>
                  <div className="db2-nw-hero-big">
                    <span className="db2-nw-hero-cur">{cur}</span>
                    {summary.netWorth !== null ? formatLargeNumber(summary.netWorth, cur) : summaryLoading ? "…" : "0"}
                  </div>
                  <div className="db2-nw-hero-pills">
                    <TrendPill value={summary.netWorthChangePercentage} />
                    {absoluteNWChange != null && (
                      <span className={`db2-abs-change ${Number(summary.netWorthChangePercentage) >= 0 ? "pos" : "neg"}`}>
                        {Number(summary.netWorthChangePercentage) >= 0 ? "+" : "−"}{cur} {formatLargeNumber(Math.abs(absoluteNWChange), cur)}
                      </span>
                    )}
                  </div>
                </div>
                <PeriodTabs
                  value={nwPeriod}
                  onChange={(o) => setNwPeriod(o.value)}
                  options={[
                    { value: "daily",   label: "Day"   },
                    { value: "weekly",  label: "Week"  },
                    { value: "monthly", label: "Month" },
                    { value: "yearly",  label: "Year"  },
                  ]}
                />
              </div>
              <div className="db2-chart-area" style={{ height: 200, marginTop: 14 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 4, right: 56, left: 8, bottom: 0 }}>
                    <defs>
                      <linearGradient id="nwHeroFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%"   stopColor="#818cf8" stopOpacity="0.38" />
                        <stop offset="60%"  stopColor="#6366f1" stopOpacity="0.15" />
                        <stop offset="100%" stopColor="#4f46e5" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey={nwPeriod === "yearly" ? "year" : "day"}
                      axisLine={false} tickLine={false}
                      tick={{ fill: "rgba(255,255,255,0.35)", fontSize: 10, fontWeight: 600 }} height={24} />
                    <YAxis orientation="right" width={52} axisLine={false} tickLine={false}
                      tickCount={4}
                      tick={{ fill: "rgba(255,255,255,0.25)", fontSize: 9, fontWeight: 600 }}
                      tickFormatter={(v) => v > 0 ? formatLargeNumber(v, cur) : "0"} />
                    <Tooltip content={<BarTooltip />} cursor={{ stroke: "rgba(255,255,255,0.12)", strokeWidth: 1 }} />
                    <Area type="monotone" dataKey="value" name="Net Worth"
                      stroke="#818cf8" strokeWidth={2.5}
                      fill="url(#nwHeroFill)" dot={false}
                      activeDot={{ r: 5, fill: "#818cf8", stroke: "rgba(255,255,255,0.4)", strokeWidth: 2 }} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* ══ ROW 3: Portfolio Details | Journey+AVL Combo | Asset Distribution (tall) ══ */}

            {/* Portfolio Details */}
            <div className="db2-card">
              <div className="db2-card-header" style={{ marginBottom: 8 }}>
                <div className="db2-card-header-left">
                  <div className="db2-card-label"><Wallet size={11} /> Portfolio</div>
                  <h6 className="db2-card-title">Portfolio Details</h6>
                  <div style={{ display: "flex", gap: 5, marginTop: 3 }}>
                    <span className="db2-tag-chip">{summary.currency || "—"}</span>
                    <span className="db2-tag-chip active">Active</span>
                  </div>
                </div>
              </div>
              <div className="db2-port-spec-row">
                <div className="db2-port-spec">
                  <div className="db2-port-spec-val">{cur} {summary.netWorth != null ? formatLargeNumber(summary.netWorth, cur) : "—"}</div>
                  <div className="db2-port-spec-label">Net Worth</div>
                </div>
                <div className="db2-port-spec">
                  <div className="db2-port-spec-val">{summary.totalAssets?.count || 0}</div>
                  <div className="db2-port-spec-label">Assets</div>
                </div>
                <div className="db2-port-spec">
                  <div className="db2-port-spec-val">{summary.totalLiabilities?.count || 0}</div>
                  <div className="db2-port-spec-label">Debts</div>
                </div>
              </div>
              <div className="db2-port-divider" />
              <div className="db2-card-label" style={{ marginBottom: 8 }}>Portfolio Owner</div>
              <div className="db2-port-owner">
                <div className="db2-port-avatar">
                  {(user?.name || user?.email || "P")[0].toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="db2-port-owner-name">{user?.name || user?.email?.split("@")[0] || "Portfolio Owner"}</div>
                  <div className="db2-port-owner-email">{user?.email || "—"}</div>
                </div>
                <button className="db2-port-action-btn" onClick={() => navigate("/editprofile")}>
                  <ArrowUpRight size={14} />
                </button>
              </div>
              {cur && (
                <div className="db2-port-currency-badge" style={{ marginTop: 14 }}>
                  <span style={{ fontSize: 13, fontWeight: 800 }}>{cur}</span>
                  <span style={{ color: "#818cf8", fontSize: 10, fontWeight: 600 }}>· Active Currency</span>
                </div>
              )}
            </div>

            {/* Journey + AVL Chart Combo — creative combination card */}
            <div className="db2-card">
              <div className="db2-card-header" style={{ marginBottom: 10 }}>
                <div className="db2-card-header-left">
                  <div className="db2-card-label"><TrendingUp size={11} /> Wealth Tracker</div>
                  <h6 className="db2-card-title">Portfolio Journey</h6>
                </div>
                <span className="db2-view-more" onClick={() => navigate("/my-assets")}>View more ›</span>
              </div>

              {/* Compact route visualization */}
              <div className="db2-journey-route" style={{ padding: "12px 16px", marginBottom: 10 }}>
                <div className="db2-journey-city">
                  <span className="db2-city-tag">START</span>
                  <span className="db2-city-sub">{cur} {periodStartVal > 0 ? formatLargeNumber(periodStartVal, cur) : "0"}</span>
                </div>
                <div className="db2-journey-middle">
                  <div className="db2-journey-icon-wrap" style={{ width: 34, height: 34 }}>
                    <TrendingUp size={15} strokeWidth={2} />
                  </div>
                  <div className="db2-journey-progress-row">
                    <span>0</span>
                    <div className="db2-journey-bar"><div className="db2-journey-bar-fill" style={{ width: `${yearProgress}%` }} /></div>
                    <span>{yearProgress}%</span>
                  </div>
                  <div className="db2-journey-times"><span>Jan</span><span>→</span><span>Now</span></div>
                </div>
                <div className="db2-journey-city" style={{ textAlign: "right" }}>
                  <span className="db2-city-tag">NOW</span>
                  <span className="db2-city-sub">{cur} {summary.netWorth != null ? formatLargeNumber(summary.netWorth, cur) : "…"}</span>
                </div>
              </div>

              {/* Compact 2×2 timeline grid */}
              <div className="db2-timeline-grid">
                <div className="db2-tl-item-compact">
                  <span className="db2-tl-dot done" />{summary.totalAssets?.count || 0} Assets
                </div>
                <div className="db2-tl-item-compact">
                  <span className="db2-tl-dot done" />{summary.totalLiabilities?.count || 0} Liabilities
                </div>
                <div className="db2-tl-item-compact active">
                  <span className="db2-tl-dot active" />{cur} {summary.netWorth != null ? formatLargeNumber(summary.netWorth, cur) : "—"}
                </div>
                <div className="db2-tl-item-compact">
                  <span className="db2-tl-dot" />{formatLastUpdated(summary.lastUpdated)}
                </div>
              </div>

              {/* ── AVL Chart section ── */}
              <div className="db2-combo-chart-label">
                <div className="db2-card-label" style={{ fontSize: 9 }}>
                  Asset vs Liability Breakdown
                </div>
                <div className="db2-avl-legend" style={{ margin: 0 }}>
                  <div className="db2-avl-legend-item">
                    <div className="db2-avl-legend-dot" style={{ background: "#4f46e5" }} />Assets
                  </div>
                  <div className="db2-avl-legend-item">
                    <div className="db2-avl-legend-dot" style={{ background: "#f97316" }} />Liabilities
                  </div>
                </div>
              </div>
              <div className="db2-chart-area" style={{ height: 110 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={avlChartData} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="avlA2" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#6366f1" stopOpacity="0.25" />
                        <stop offset="100%" stopColor="#6366f1" stopOpacity="0" />
                      </linearGradient>
                      <linearGradient id="avlL2" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#f97316" stopOpacity="0.22" />
                        <stop offset="100%" stopColor="#f97316" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey={avlXKey} axisLine={false} tickLine={false}
                      tick={{ fill: "#9898b0", fontSize: 9, fontWeight: 600 }} height={18}
                      interval="preserveStartEnd" />
                    <YAxis orientation="right" width={44} axisLine={false} tickLine={false}
                      tickCount={3}
                      tick={{ fill: "#c4c4d0", fontSize: 8, fontWeight: 600 }}
                      tickFormatter={(v) => v > 0 ? formatLargeNumber(v, cur) : "0"} />
                    <Tooltip content={<AreaTooltip />} cursor={false} />
                    <Area name="Assets" type="monotone" dataKey="assets"
                      stroke="#4f46e5" strokeWidth={1.8} fill="url(#avlA2)" dot={false}
                      activeDot={{ r: 3, fill: "#4f46e5", strokeWidth: 0 }} />
                    <Area name="Liabilities" type="monotone" dataKey="liabilities"
                      stroke="#f97316" strokeWidth={1.8} fill="url(#avlL2)" dot={false}
                      activeDot={{ r: 3, fill: "#f97316", strokeWidth: 0 }} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Asset Distribution — full height, spans rows 2+3 — like Map Overview */}
            <div className="db2-card db2-row-span-2 db2-dist-card">
              <div className="db2-card-header" style={{ marginBottom: 10 }}>
                <div className="db2-card-header-left">
                  <div className="db2-card-label"><LayoutDashboard size={11} /> Portfolio Mix</div>
                  <h6 className="db2-card-title">Asset Distribution</h6>
                </div>
              </div>
              <div style={{ flex: 1, minHeight: 0 }}>
                <AssetDistributionChart />
              </div>
              <div className="db2-dist-footer">
                <div>
                  <span className="db2-dist-footer-num">{cur} {formatLargeNumber(summary.totalAssets?.value ?? 0, cur)}</span>
                  <span className="db2-dist-footer-label">{summary.totalAssets?.count || 0} assets total</span>
                </div>
                <div style={{ textAlign: "right" }}>
                  <span className="db2-dist-footer-num">{cur} {formatLargeNumber(summary.totalLiabilities?.value ?? 0, cur)}</span>
                  <span className="db2-dist-footer-label">liabilities</span>
                </div>
              </div>
            </div>

            {/* ══ ROW 3: Diversification Gauge | Top Performer ══ */}

            {/* Diversification Gauge — dual-ring style (reference image 3) */}
            <div className="db2-card db2-divers-card">
              <div style={{ width: "100%" }}>
                <div className="db2-card-label"><LayoutDashboard size={11} /> Score</div>
                <h6 className="db2-card-title" style={{ marginTop: 4 }}>
                  Diversification <InfoTooltip content="How diversified your asset portfolio is." />
                </h6>
              </div>
              <div className="db2-gauge-legend db2-gauge-legend-light">
                <div className="db2-gauge-legend-item">
                  <span className="db2-gauge-legend-dot" style={{ background: "#0f1020" }} />
                  <span>Target (70%)</span>
                </div>
                <div className="db2-gauge-legend-item">
                  <span className="db2-gauge-legend-dot" style={{ background: "#4f46e5" }} />
                  <span>Current</span>
                </div>
              </div>
              <div className="db2-divers-gauge-wrap">
                <DualRingGauge score={diversScore ?? 0} size={148} />
              </div>
              <div style={{ textAlign: "center", width: "100%", fontSize: 11, fontWeight: 700, color: "#9898b0", textTransform: "uppercase", letterSpacing: "0.1em" }}>
                {diversLabel}
              </div>
              <div className="db2-score-band" style={{ marginTop: 12 }}>
                {[0,1,2,3,4,5,6,7,8,9].map(i => (
                  <div key={i} className={`db2-score-band-seg${diversScore !== null && diversScore >= (i+1)*10 ? " active" : ""}`} />
                ))}
              </div>
            </div>

            {/* Top Performer — dark navy, like Vehicle card */}
            {topAssets.length > 0 ? (() => {
              const asset = topAssets[0];
              const pct = +(asset.performanceMetrics?.totalReturnPercentage ?? 0);
              return (
                <div className="db2-card db2-card-dark db2-top-card">
                  <div style={{ position: "relative", zIndex: 1, flex: 1, display: "flex", flexDirection: "column" }}>
                    <div className="db2-card-label" style={{ marginBottom: 4 }}>
                      <TrendingUp size={11} /> Top Performer
                    </div>
                    <div className="db2-top-asset-type">{asset.assetType || asset.category || "Asset"}</div>
                    <h4 className="db2-top-asset-name">{asset.name}</h4>
                    <div className="db2-spec-grid">
                      <div className="db2-spec-item">
                        <span className="db2-spec-label">Current Value</span>
                        <span className="db2-spec-val">{cur} {formatLargeNumber(asset.currentValue ?? asset.value ?? 0, cur)}</span>
                      </div>
                      <div className="db2-spec-item">
                        <span className="db2-spec-label">Total Return</span>
                        <span className="db2-spec-val pos">+{pct.toFixed(1)}%</span>
                      </div>
                      <div className="db2-spec-item">
                        <span className="db2-spec-label">Rank</span>
                        <span className="db2-spec-val">#1 Best</span>
                      </div>
                      <div className="db2-spec-item">
                        <span className="db2-spec-label">Portfolio</span>
                        <span className="db2-spec-val">{summary.totalAssets?.count || "—"} assets</span>
                      </div>
                    </div>
                  </div>
                  <div className="db2-top-visual"><TrendingUp size={110} strokeWidth={0.6} /></div>
                </div>
              );
            })() : (
              <div className="db2-card db2-card-dark db2-top-card">
                <div className="db2-card-label"><TrendingUp size={11} /> Top Performer</div>
                <div className="db2-empty"><TrendingUp size={28} strokeWidth={1.2} />No asset data yet</div>
                <div className="db2-top-visual"><TrendingUp size={110} strokeWidth={0.6} /></div>
              </div>
            )}

            {/* ══ ROW 5: New Chart Showcase ══ */}

            {/* Chart 1 — Rounded Pill Bar with diagonal hatching */}
            <div className="db2-card db2-pill-bar-card">
              <div className="db2-card-header" style={{ marginBottom: 10 }}>
                <div className="db2-card-header-left">
                  <div className="db2-card-label"><Activity size={11} /> Net Worth</div>
                  <h6 className="db2-card-title">Monthly Performance</h6>
                </div>
                <PeriodTabs
                  value={pillPeriod}
                  onChange={(o) => setPillPeriod(o.value)}
                  options={[
                    { value: "monthly", label: "Monthly" },
                    { value: "yearly",  label: "Annually" },
                  ]}
                />
              </div>
              <div style={{ flex: 1, minHeight: 180 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={pillChartData} margin={{ top: 28, right: 8, left: 0, bottom: 0 }} barCategoryGap="20%">
                    <defs>
                      <pattern id="pillHatchNorm" patternUnits="userSpaceOnUse" width="7" height="7" patternTransform="rotate(45 0 0)">
                        <rect width="7" height="7" fill="rgba(197,202,254,0.55)"/>
                        <line x1="0" y1="0" x2="0" y2="7" stroke="rgba(79,70,229,0.4)" strokeWidth="2.5"/>
                      </pattern>
                      <pattern id="pillHatchActv" patternUnits="userSpaceOnUse" width="7" height="7" patternTransform="rotate(45 0 0)">
                        <rect width="7" height="7" fill="rgba(79,70,229,0.75)"/>
                        <line x1="0" y1="0" x2="0" y2="7" stroke="rgba(49,46,129,0.6)" strokeWidth="2.5"/>
                      </pattern>
                    </defs>
                    <CartesianGrid vertical={false} strokeDasharray="4 4" stroke="#eaecf5" />
                    <XAxis dataKey={pillPeriod === "yearly" ? "year" : "day"}
                      axisLine={false} tickLine={false}
                      tick={{ fill: "#9898b0", fontSize: 10, fontWeight: 600 }} height={24} />
                    <YAxis axisLine={false} tickLine={false} width={44}
                      tick={{ fill: "#c4c4d0", fontSize: 9, fontWeight: 600 }}
                      tickFormatter={(v) => v > 0 ? formatLargeNumber(v, cur) : "0"} />
                    <Tooltip content={<BarTooltip />} cursor={{ fill: "rgba(79,70,229,0.04)" }} />
                    <Bar dataKey="value" radius={[18, 18, 18, 18]}
                      label={(props) => {
                        const { x, y, width, index } = props;
                        if (index !== pillActiveBarIdx || pillActivePct == null) return <g />;
                        const px = x + width / 2;
                        return (
                          <g>
                            <rect x={px - 30} y={y - 34} width={60} height={22} rx={11} fill="#0f1020"/>
                            <text x={px} y={y - 19} textAnchor="middle" fill="#fff" fontSize={10} fontWeight={700} fontFamily="inherit">
                              {pillActivePct >= 0 ? "+" : ""}{pillActivePct.toFixed(1)}%
                            </text>
                          </g>
                        );
                      }}
                    >
                      {pillChartData.map((_, i) => (
                        <Cell key={i} fill={i === pillActiveBarIdx ? "url(#pillHatchActv)" : "url(#pillHatchNorm)"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Chart 2 — Balance Wave Card (large value + smooth wave) */}
            <div className="db2-card db2-balance-wave-card">
              <div className="db2-card-header" style={{ marginBottom: 0 }}>
                <div className="db2-card-header-left">
                  <div className="db2-card-label"><Wallet size={11} /> Portfolio</div>
                  <h6 className="db2-card-title">Total Balance</h6>
                </div>
                <button className="db2-port-action-btn" onClick={() => navigate("/my-assets")}>
                  <ArrowUpRight size={14} />
                </button>
              </div>
              <div className="db2-balance-center">
                <div className="db2-balance-sub">Total Net Worth</div>
                <div className="db2-balance-big">
                  <span className="db2-balance-cur">{cur}</span>
                  {summary.netWorth != null ? formatLargeNumber(summary.netWorth, cur) : summaryLoading ? "…" : "0"}
                </div>
                <TrendPill value={summary.netWorthChangePercentage} />
              </div>
              <div className="db2-balance-wave">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="balWave" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%"   stopColor="#4f46e5" stopOpacity="0.2" />
                        <stop offset="100%" stopColor="#4f46e5" stopOpacity="0.01" />
                      </linearGradient>
                    </defs>
                    <Area type="monotone" dataKey="value"
                      stroke="#4f46e5" strokeWidth={2}
                      fill="url(#balWave)" dot={false} activeDot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="db2-balance-actions">
                <button className="db2-balance-btn primary" onClick={() => navigate("/add-assets")}>
                  Add Asset <ArrowUpRight size={13} />
                </button>
                <button className="db2-balance-btn secondary" onClick={() => navigate("/my-liability")}>
                  Liabilities <ArrowDownRight size={13} />
                </button>
              </div>
            </div>

            {/* Chart 4 — Dark Analytics Bar with category filter tabs */}
            <div className="db2-card db2-dark-analytics-card">
              <div className="db2-card-header" style={{ marginBottom: 10 }}>
                <div className="db2-card-header-left">
                  <div className="db2-card-label"><Activity size={11} /> Asset Analytics</div>
                  <h6 className="db2-card-title">Performance Breakdown</h6>
                </div>
              </div>
              {(() => {
                const darkDataKey = darkFilter === "Assets" ? "assets"
                  : darkFilter === "Liabilities" ? "liabilities"
                  : "netWorth";
                const darkHighlightColor = darkFilter === "Assets" ? "#6366f1"
                  : darkFilter === "Liabilities" ? "#f97316"
                  : "#10b981";
                const darkDimColor = darkFilter === "Assets" ? "rgba(99,102,241,0.13)"
                  : darkFilter === "Liabilities" ? "rgba(249,115,22,0.13)"
                  : "rgba(16,185,129,0.13)";
                const darkBarData = avlChartData.slice(-14).map(d => ({
                  ...d,
                  netWorth: Math.max(0, (d.assets || 0) - (d.liabilities || 0)),
                }));
                const maxVal = Math.max(...darkBarData.map(x => x[darkDataKey] || 0));
                return (
                  <div className="db2-analytics-dark" style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                    <div className="db2-analytics-header">
                      <div className="db2-analytics-title"><Activity size={10} /> by period</div>
                      <div className="db2-analytics-tabs">
                        {["weekly", "monthly"].map(p => (
                          <button key={p}
                            className={`db2-analytics-tab${avlPeriod === p ? " active" : ""}`}
                            onClick={() => setAvlPeriod(p)}>
                            {p === "weekly" ? "Week" : "Month"}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div style={{ flex: 1, minHeight: 140 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={darkBarData} margin={{ top: 4, right: 0, left: 0, bottom: 0 }} barCategoryGap="35%">
                          <Tooltip content={<BarTooltip />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                          <Bar dataKey={darkDataKey} radius={[5, 5, 0, 0]}>
                            {darkBarData.map((d, i) => {
                              const isHighlight = maxVal > 0 && (d[darkDataKey] || 0) >= maxVal * 0.75;
                              return <Cell key={i} fill={isHighlight ? darkHighlightColor : darkDimColor} />;
                            })}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="db2-analytics-filter-row">
                      {[
                        { label: "Assets",      activeClass: "active" },
                        { label: "Liabilities", activeClass: "active-liab" },
                        { label: "Net Worth",   activeClass: "active-nw" },
                      ].map(({ label, activeClass }) => (
                        <button key={label}
                          className={`db2-analytics-filter-chip${darkFilter === label ? ` ${activeClass}` : ""}`}
                          onClick={() => setDarkFilter(label)}>
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* ══ ROW 5b: Bubble Pack (span 2) | Waffle Grid (span 1) ══ */}

            {/* Bubble Pack */}
            {bubblePackData.length > 0 && (
              <div className="db2-card db2-bubble-card" style={{ gridColumn: "span 2" }}>
                <div className="db2-card-header">
                  <span className="db2-card-label">Portfolio Bubbles</span>
                  <div className="db2-scatter-legend">
                    <span><span className="db2-scatter-dot" style={{ background: "#4f46e5" }} />Positive return</span>
                    <span><span className="db2-scatter-dot" style={{ background: "#ef4444" }} />Negative return</span>
                  </div>
                </div>
                <div className="db2-bubble-wrap">
                  <svg viewBox="0 0 420 260" width="100%" height="260" style={{ overflow: "visible" }}>
                    <defs>
                      {bubblePackData.map((b, i) => (
                        <radialGradient key={i} id={`bg${i}`} cx="40%" cy="35%" r="65%">
                          <stop offset="0%" stopColor={b.ret >= 0 ? "#6366f1" : "#f87171"} stopOpacity="0.9" />
                          <stop offset="100%" stopColor={b.ret >= 0 ? "#3730a3" : "#dc2626"} stopOpacity="0.75" />
                        </radialGradient>
                      ))}
                    </defs>
                    {bubblePackData.map((b, i) => (
                      <g key={i} style={{ cursor: "pointer" }}>
                        <circle cx={b.x} cy={b.y} r={b.r} fill={`url(#bg${i})`} />
                        <circle cx={b.x} cy={b.y} r={b.r} fill="none" stroke="#fff" strokeOpacity="0.2" strokeWidth="1.5" />
                        {b.r > 34 && (
                          <text x={b.x} y={b.y - 6} textAnchor="middle" fontSize={Math.min(11, b.r / 4)} fontWeight={700} fill="#fff" fontFamily="inherit">{b.name}</text>
                        )}
                        {b.r > 34 && (
                          <text x={b.x} y={b.y + 11} textAnchor="middle" fontSize={Math.min(10, b.r / 4.5)} fill="rgba(255,255,255,0.75)" fontFamily="inherit">
                            {b.ret >= 0 ? "+" : ""}{b.ret.toFixed(1)}%
                          </text>
                        )}
                      </g>
                    ))}
                  </svg>
                </div>
              </div>
            )}

            {/* Waffle Grid — Debt vs Equity */}
            <div className="db2-card db2-waffle-card">
              <div className="db2-card-header">
                <span className="db2-card-label">Debt vs Equity</span>
              </div>
              <div className="db2-waffle-grid">
                {Array.from({ length: 100 }, (_, i) => (
                  <div key={i} className={`db2-waffle-cell ${i < (debtRatio ?? 0) ? "debt" : "equity"}`} />
                ))}
              </div>
              <div className="db2-waffle-stats">
                <div className="db2-waffle-stat">
                  <span className="db2-waffle-dot-lg equity" />
                  <div>
                    <div className="db2-waffle-stat-val" style={{ color: "#4f46e5" }}>
                      {debtRatio !== null ? 100 - debtRatio : 100}%
                    </div>
                    <div className="db2-waffle-stat-label">Equity</div>
                  </div>
                </div>
                <div className="db2-waffle-divider" />
                <div className="db2-waffle-stat">
                  <span className="db2-waffle-dot-lg debt" />
                  <div>
                    <div className="db2-waffle-stat-val" style={{ color: "#ef4444" }}>
                      {debtRatio ?? 0}%
                    </div>
                    <div className="db2-waffle-stat-label">Debt</div>
                  </div>
                </div>
              </div>
            </div>

            {/* ══ ROW 5c: Portfolio Rings (span 1) | Treemap (span 2) ══ */}

            {/* Sunburst / Portfolio Rings */}
            {(() => {
              if (!sunburstData?.length) return null;
              const totalVal = sunburstData.reduce((s, d) => s + d.value, 0);
              if (!totalVal) return null;
              const COLORS = ["#4f46e5","#7c3aed","#0ea5e9","#10b981","#f59e0b","#ef4444","#ec4899"];
              const cx = 120, cy = 120, W = 240;
              const toXY = (r, angle) => ({
                x: cx + r * Math.cos((angle - 90) * Math.PI / 180),
                y: cy + r * Math.sin((angle - 90) * Math.PI / 180),
              });
              const arcD = (r, startA, endA) => {
                const s = toXY(r, startA), e = toXY(r, endA);
                const large = endA - startA > 180 ? 1 : 0;
                return `M ${s.x.toFixed(2)} ${s.y.toFixed(2)} A ${r} ${r} 0 ${large} 1 ${e.x.toFixed(2)} ${e.y.toFixed(2)}`;
              };
              let angle = 0;
              const arcs = sunburstData.map((cat, ci) => {
                const sweep = (cat.value / totalVal) * 360;
                const gap = Math.max(1.5, sweep * 0.04);
                const startA = angle + gap / 2;
                const endA = angle + sweep - gap / 2;
                let assetAngle = startA;
                const assetArcs = cat.assets.map((a, ai) => {
                  const asw = Math.max(0, (a.value / cat.value) * (sweep - gap) - 0.8);
                  const as = assetAngle, ae = as + asw;
                  assetAngle += asw + 0.8;
                  return { as, ae };
                });
                angle += sweep;
                return { startA, endA, color: COLORS[ci % COLORS.length], name: cat.name, value: cat.value, assetArcs };
              });
              return (
                <div className="db2-card db2-sunburst-card">
                  <div className="db2-card-header">
                    <span className="db2-card-label">Portfolio Rings</span>
                  </div>
                  <div className="db2-sunburst-inner">
                    <svg viewBox={`0 0 ${W} ${W}`} width={W} height={W} style={{ flexShrink: 0 }}>
                      {arcs.map((cat, ci) => (
                        <g key={ci}>
                          {cat.assetArcs.map((a, ai) => a.ae - a.as > 0.5 && (
                            <path key={ai} d={arcD(108, a.as, a.ae)}
                              fill="none" stroke={cat.color} strokeWidth={14}
                              strokeOpacity={0.55 + (ai % 2) * 0.2} strokeLinecap="round" />
                          ))}
                          {cat.endA - cat.startA > 0.5 && (
                            <path d={arcD(82, cat.startA, cat.endA)}
                              fill="none" stroke={cat.color} strokeWidth={20} strokeLinecap="round" />
                          )}
                        </g>
                      ))}
                      <text x={cx} y={cy - 9} textAnchor="middle" fontSize={13} fontWeight={800} fill="#1a1a3e" fontFamily="inherit">Portfolio</text>
                      <text x={cx} y={cy + 11} textAnchor="middle" fontSize={10} fill="#9898b0" fontFamily="inherit">{sunburstData.length} categories</text>
                    </svg>
                    <div className="db2-sunburst-legend">
                      {arcs.map((cat, i) => (
                        <div key={i} className="db2-sunburst-legend-row">
                          <span className="db2-sunburst-dot" style={{ background: cat.color }} />
                          <span className="db2-sunburst-name">{cat.name}</span>
                          <span className="db2-sunburst-pct">{Math.round((cat.value / totalVal) * 100)}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Portfolio Treemap */}
            {(() => {
              const allA = [...topAssets, ...underAssets];
              if (!allA.length) return null;
              const tmData = allA.map(a => ({
                name: (a.name || "Asset").slice(0, 16),
                size: a.currentValue || a.value || 1,
                ret: +(a.performanceMetrics?.totalReturnPercentage ?? 0),
              }));
              return (
                <div className="db2-card db2-treemap-card" style={{ gridColumn: "span 2" }}>
                  <div className="db2-card-header">
                    <span className="db2-card-label">Portfolio Treemap</span>
                    <div className="db2-scatter-legend">
                      <span><span className="db2-scatter-dot" style={{ background: "#4f46e5" }} />High return</span>
                      <span><span className="db2-scatter-dot" style={{ background: "#10b981" }} />Positive</span>
                      <span><span className="db2-scatter-dot" style={{ background: "#ef4444" }} />Negative</span>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={240}>
                    <Treemap data={tmData} dataKey="size" aspectRatio={16 / 7} stroke="#fff" strokeWidth={3}
                      content={({ x, y, width, height, name, ret }) => {
                        if (!width || !height || width < 2 || height < 2) return null;
                        const fill = ret > 10 ? "#4f46e5" : ret > 0 ? "#10b981" : ret > -5 ? "#f59e0b" : "#ef4444";
                        const opacity = 0.45 + Math.min(0.45, Math.abs(ret ?? 0) / 22);
                        return (
                          <g>
                            <rect x={x + 2} y={y + 2} width={width - 4} height={height - 4}
                              fill={fill} fillOpacity={opacity} rx={8} />
                            {width > 55 && height > 36 && (
                              <text x={x + width / 2} y={y + height / 2 - 7}
                                textAnchor="middle" fontSize={Math.min(12, width / 8)} fontWeight={700} fill="#fff" fontFamily="inherit">
                                {name}
                              </text>
                            )}
                            {width > 55 && height > 50 && (
                              <text x={x + width / 2} y={y + height / 2 + 11}
                                textAnchor="middle" fontSize={Math.min(11, width / 9)} fill="rgba(255,255,255,0.78)" fontFamily="inherit">
                                {ret != null ? `${ret >= 0 ? "+" : ""}${ret.toFixed(1)}%` : ""}
                              </text>
                            )}
                          </g>
                        );
                      }}
                    />
                  </ResponsiveContainer>
                </div>
              );
            })()}

            {/* ══ ROW 5d: NW Composition Ring | Performance Podium | Health Score Tiles ══ */}

            {/* Wealth Composition Ring */}
            {(() => {
              const totalA = summary.totalAssets?.value || 0;
              const totalL = summary.totalLiabilities?.value || 0;
              const nwVal  = summary.netWorth || 0;
              if (!totalA) return null;
              const nwPct   = Math.max(0, Math.min(100, (nwVal / totalA) * 100));
              const debtPct = Math.max(0, Math.min(100, (totalL / totalA) * 100));
              const cx = 100, cy = 100, r = 72;
              const circ = 2 * Math.PI * r;
              const offset = circ * 0.25;
              return (
                <div className="db2-card db2-nwring-card">
                  <div className="db2-card-header">
                    <span className="db2-card-label">Wealth Composition</span>
                  </div>
                  <div className="db2-nwring-wrap">
                    <svg viewBox="0 0 200 200" width={176} height={176} style={{ flexShrink: 0 }}>
                      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#f0f0f5" strokeWidth={22} />
                      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#10b981" strokeWidth={22}
                        strokeDasharray={`${(nwPct / 100) * circ} ${circ}`}
                        strokeDashoffset={offset} strokeLinecap="round" />
                      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#ef4444" strokeWidth={22}
                        strokeDasharray={`${(debtPct / 100) * circ} ${circ}`}
                        strokeDashoffset={offset - (nwPct / 100) * circ}
                        strokeLinecap="round" opacity={0.8} />
                      <text x={cx} y={cy - 9} textAnchor="middle" fontSize={24} fontWeight={800} fill="#1a1a3e" fontFamily="inherit">
                        {Math.round(nwPct)}%
                      </text>
                      <text x={cx} y={cy + 13} textAnchor="middle" fontSize={10} fill="#9898b0" fontFamily="inherit">
                        net worth ratio
                      </text>
                    </svg>
                    <div className="db2-nwring-legend">
                      {[
                        { color: "#10b981", val: nwVal,  label: "Net Worth" },
                        { color: "#ef4444", val: totalL, label: "Liabilities" },
                        { color: "#4f46e5", val: totalA, label: "Assets" },
                      ].map(({ color, val, label }) => (
                        <div key={label} className="db2-nwring-item">
                          <span className="db2-nwring-dot" style={{ background: color }} />
                          <div>
                            <div className="db2-nwring-val">{cur} {formatLargeNumber(val, cur)}</div>
                            <div className="db2-nwring-label">{label}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Performance Podium */}
            {(() => {
              const top = topAssets.slice(0, 3);
              const bot = underAssets.slice(0, 3);
              if (!top.length) return null;
              const podiumOrder  = [1, 0, 2];
              const podiumHeights = [88, 128, 64];
              const podiumColors  = ["#818cf8", "#4f46e5", "#a5b4fc"];
              return (
                <div className="db2-card db2-podium-card">
                  <div className="db2-card-header">
                    <span className="db2-card-label">Performance Podium</span>
                  </div>
                  <div className="db2-podium-wrap">
                    <div className="db2-podium-top">
                      {podiumOrder.map((pos, i) => {
                        const a = top[pos];
                        if (!a) return <div key={i} className="db2-podium-slot" style={{ flex: 1 }} />;
                        const ret = +(a.performanceMetrics?.totalReturnPercentage ?? 0);
                        return (
                          <div key={i} className="db2-podium-slot">
                            <div className="db2-podium-name">{(a.name || "").slice(0, 13)}</div>
                            <div className="db2-podium-ret">+{ret.toFixed(1)}%</div>
                            <div className="db2-podium-block" style={{ height: podiumHeights[i], background: podiumColors[i] }}>
                              <span className="db2-podium-rank">{pos + 1}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="db2-podium-ground" />
                    {bot.length > 0 && (
                      <div className="db2-podium-bottom">
                        {bot.map((a, i) => {
                          const ret = +(a.performanceMetrics?.totalReturnPercentage ?? 0);
                          return (
                            <div key={i} className="db2-podium-loss">
                              <div className="db2-podium-loss-name">{(a.name || "").slice(0, 13)}</div>
                              <div className="db2-podium-loss-ret">{ret.toFixed(1)}%</div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Health Score Tiles */}
            {radarData.some(d => d.value > 0) && (
              <div className="db2-card db2-health-tiles-card">
                <div className="db2-card-header">
                  <span className="db2-card-label">Health Scores</span>
                </div>
                <div className="db2-health-tiles">
                  {radarData.map((d, i) => {
                    const tier = d.value >= 70 ? "good" : d.value >= 40 ? "warn" : "poor";
                    return (
                      <div key={i} className={`db2-health-tile ${tier}`}>
                        <div className="db2-health-tile-score">{d.value}</div>
                        <div className="db2-health-tile-axis">{d.axis}</div>
                        <div className="db2-health-tile-track">
                          <div className="db2-health-tile-fill" style={{ height: `${d.value}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ══ ROW 5e: Return Spectrum (full width) ══ */}
            {(() => {
              const allA = [...topAssets, ...underAssets];
              if (!allA.length) return null;
              const pts = allA.map(a => ({
                name: (a.name || "Asset").slice(0, 13),
                ret: +(a.performanceMetrics?.totalReturnPercentage ?? 0),
              })).sort((a, b) => a.ret - b.ret);
              const minR = pts[0].ret, maxR = pts[pts.length - 1].ret;
              const range = maxR - minR || 1;
              const norm = v => ((v - minR) / range) * 100;
              return (
                <div className="db2-card db2-span-3 db2-spectrum-card">
                  <div className="db2-card-header">
                    <span className="db2-card-label">Return Spectrum</span>
                    <span className="db2-card-sub">Assets ranked by performance — left = worst, right = best</span>
                  </div>
                  <div className="db2-spectrum-scene">
                    <div className="db2-spectrum-bar">
                      <div className="db2-spectrum-gradient" />
                      {pts.map((p, i) => {
                        const pct = norm(p.ret);
                        const above = i % 2 === 0;
                        return (
                          <div key={i} className={`db2-spectrum-pin ${above ? "up" : "down"}`}
                            style={{ left: `${pct}%` }}>
                            {above && <div className="db2-spectrum-pin-label">{p.name}</div>}
                            {above && <div className={`db2-spectrum-pin-ret ${p.ret >= 0 ? "pos" : "neg"}`}>
                              {p.ret >= 0 ? "+" : ""}{p.ret.toFixed(1)}%
                            </div>}
                            <div className="db2-spectrum-stem" />
                            <div className={`db2-spectrum-dot ${p.ret >= 0 ? "pos" : "neg"}`} />
                            {!above && <div className="db2-spectrum-stem" />}
                            {!above && <div className={`db2-spectrum-pin-ret ${p.ret >= 0 ? "pos" : "neg"}`}>
                              {p.ret >= 0 ? "+" : ""}{p.ret.toFixed(1)}%
                            </div>}
                            {!above && <div className="db2-spectrum-pin-label">{p.name}</div>}
                          </div>
                        );
                      })}
                    </div>
                    <div className="db2-spectrum-axis">
                      <span style={{ color: "#ef4444" }}>{minR.toFixed(1)}%</span>
                      <span style={{ color: "#9898b0" }}>0%</span>
                      <span style={{ color: "#10b981" }}>+{maxR.toFixed(1)}%</span>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* ══ ROW 5f: Hex Grid (1) | Candlestick (2) ══ */}

            {/* Asset Hex Grid */}
            {(() => {
              const allA = [...topAssets, ...underAssets];
              if (!allA.length) return null;
              const R = 36;
              const dX = R * 1.82, dY = R * Math.sqrt(3) * 0.9;
              const cols = 3;
              const positions = allA.map((_, i) => ({
                x: (i % cols) * dX + ((Math.floor(i / cols)) % 2) * (dX / 2) + R * 1.1,
                y: Math.floor(i / cols) * dY + R,
              }));
              const maxPos = positions[positions.length - 1];
              const svgW = cols * dX + R * 1.5;
              const svgH = (maxPos?.y || R) + R * 1.2;
              const hexPts = (cx, cy, r) =>
                Array.from({ length: 6 }, (_, i) => {
                  const a = (60 * i - 30) * Math.PI / 180;
                  return `${(cx + r * Math.cos(a)).toFixed(1)},${(cy + r * Math.sin(a)).toFixed(1)}`;
                }).join(' ');
              return (
                <div className="db2-card db2-hex-card">
                  <div className="db2-card-header">
                    <span className="db2-card-label">Asset Hex Grid</span>
                  </div>
                  <div className="db2-hex-wrap">
                    <svg viewBox={`0 0 ${svgW} ${svgH}`} width="100%" height={svgH}>
                      <defs>
                        {allA.map((a, i) => {
                          const ret = +(a.performanceMetrics?.totalReturnPercentage ?? 0);
                          const c1 = ret > 10 ? "#6366f1" : ret > 0 ? "#34d399" : ret > -5 ? "#fbbf24" : "#f87171";
                          const c2 = ret > 10 ? "#3730a3" : ret > 0 ? "#059669" : ret > -5 ? "#d97706" : "#dc2626";
                          return (
                            <radialGradient key={i} id={`hx${i}`} cx="35%" cy="30%" r="70%">
                              <stop offset="0%" stopColor={c1} />
                              <stop offset="100%" stopColor={c2} stopOpacity="0.85" />
                            </radialGradient>
                          );
                        })}
                      </defs>
                      {allA.map((a, i) => {
                        const { x, y } = positions[i];
                        const ret = +(a.performanceMetrics?.totalReturnPercentage ?? 0);
                        const name = (a.name || "").slice(0, 9);
                        return (
                          <g key={i}>
                            <polygon points={hexPts(x, y, R - 2)} fill={`url(#hx${i})`} />
                            <polygon points={hexPts(x, y, R - 2)} fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" />
                            <text x={x} y={y - 5} textAnchor="middle" fontSize={9} fontWeight={700} fill="#fff" fontFamily="inherit">{name}</text>
                            <text x={x} y={y + 10} textAnchor="middle" fontSize={9} fill="rgba(255,255,255,0.8)" fontFamily="inherit">
                              {ret >= 0 ? "+" : ""}{ret.toFixed(1)}%
                            </text>
                          </g>
                        );
                      })}
                    </svg>
                  </div>
                </div>
              );
            })()}

            {/* Candlestick — monthly NW direction */}
            {(() => {
              const candles = chartData.filter(d => d.value > 0).slice(-10);
              if (candles.length < 2) return null;
              const withOpen = candles.map((d, i) => ({
                ...d,
                open: candles[i - 1]?.value || d.value,
                close: d.value,
                isUp: d.value >= (candles[i - 1]?.value || d.value),
              }));
              const maxV = Math.max(...withOpen.map(d => Math.max(d.open, d.close)));
              const minV = Math.min(...withOpen.map(d => Math.min(d.open, d.close)));
              const range = maxV - minV || 1;
              const CH = 160, PAD = 12;
              const norm = v => PAD + (1 - (v - minV) / range) * (CH - PAD * 2);
              const slotW = 100 / withOpen.length;
              return (
                <div className="db2-card db2-candle-card" style={{ gridColumn: "span 2" }}>
                  <div className="db2-card-header">
                    <span className="db2-card-label">NW Monthly Candlestick</span>
                    <div className="db2-scatter-legend">
                      <span><span className="db2-scatter-dot" style={{ background: "#10b981" }} />Up month</span>
                      <span><span className="db2-scatter-dot" style={{ background: "#ef4444" }} />Down month</span>
                    </div>
                  </div>
                  <svg viewBox={`0 0 100 ${CH + 20}`} width="100%" height={CH + 20} preserveAspectRatio="none" style={{ display: "block" }}>
                    {withOpen.map((c, i) => {
                      const cx = i * slotW + slotW / 2;
                      const bw = slotW * 0.6;
                      const openY = norm(c.open);
                      const closeY = norm(c.close);
                      const bodyTop = Math.min(openY, closeY);
                      const bodyH = Math.max(1.2, Math.abs(closeY - openY));
                      const color = c.isUp ? "#10b981" : "#ef4444";
                      return (
                        <g key={i}>
                          <line x1={cx} y1={Math.min(openY, closeY) - 3} x2={cx} y2={Math.max(openY, closeY) + 3}
                            stroke={color} strokeWidth={0.4} strokeOpacity={0.5} />
                          <rect x={cx - bw / 2} y={bodyTop} width={bw} height={bodyH}
                            fill={color} fillOpacity={0.88} rx={0.8} />
                          <text x={cx} y={CH + 16} textAnchor="middle" fontSize={3.5} fill="#9898b0" fontFamily="inherit">
                            {String(c.day || "").slice(0, 3)}
                          </text>
                        </g>
                      );
                    })}
                  </svg>
                </div>
              );
            })()}

            {/* ══ ROW 5g: Dumbbell Slope (1) | Lollipop Returns (2) ══ */}

            {/* Dumbbell Slope — period comparison */}
            {(() => {
              const data = avlChartData.filter(d => d.assets || d.liabilities);
              if (data.length < 2) return null;
              const first = data[0], last = data[data.length - 1];
              const cats = [
                { name: "Assets",      before: first.assets || 0,      after: last.assets || 0,      color: "#4f46e5" },
                { name: "Liabilities", before: first.liabilities || 0, after: last.liabilities || 0, color: "#ef4444" },
              ];
              const maxV = Math.max(...cats.flatMap(c => [c.before, c.after]), 1);
              const norm = v => 8 + (v / maxV) * 84;
              const H = 140;
              const labels = [first[avlXKey] || "Start", last[avlXKey] || "Now"];
              return (
                <div className="db2-card db2-dumbbell-card">
                  <div className="db2-card-header">
                    <span className="db2-card-label">Period Comparison</span>
                  </div>
                  <div className="db2-dumbbell-labels">
                    <span>{labels[0]}</span><span>{labels[1]}</span>
                  </div>
                  <svg viewBox={`0 0 100 ${H}`} width="100%" height={H} preserveAspectRatio="none" style={{ display: "block" }}>
                    {cats.map((c, i) => {
                      const y = 30 + i * 52;
                      const x1 = norm(c.before), x2 = norm(c.after);
                      const gained = c.after > c.before;
                      return (
                        <g key={i}>
                          <text x={50} y={y - 10} textAnchor="middle" fontSize={5} fontWeight={700} fill="#6b7280" fontFamily="inherit">{c.name}</text>
                          <line x1={x1} y1={y} x2={x2} y2={y} stroke={c.color} strokeWidth={1.5} strokeOpacity={0.4} strokeDasharray={gained ? "0" : "3 2"} />
                          <circle cx={x1} cy={y} r={4} fill={c.color} fillOpacity={0.4} stroke="white" strokeWidth={1} />
                          <circle cx={x2} cy={y} r={4} fill={c.color} stroke="white" strokeWidth={1} />
                          <text x={x1 - 1} y={y + 10} textAnchor="end" fontSize={3.5} fill="#9898b0" fontFamily="inherit">{cur} {formatLargeNumber(c.before, cur)}</text>
                          <text x={x2 + 1} y={y + 10} textAnchor="start" fontSize={3.5} fill={c.color} fontWeight={700} fontFamily="inherit">{cur} {formatLargeNumber(c.after, cur)}</text>
                        </g>
                      );
                    })}
                  </svg>
                </div>
              );
            })()}

            {/* Lollipop — asset returns */}
            {(() => {
              const allA = [...topAssets, ...underAssets];
              if (!allA.length) return null;
              const sorted = [...allA].sort((a, b) =>
                +(b.performanceMetrics?.totalReturnPercentage ?? 0) -
                +(a.performanceMetrics?.totalReturnPercentage ?? 0)
              );
              const rets = sorted.map(a => +(a.performanceMetrics?.totalReturnPercentage ?? 0));
              const absMax = Math.max(...rets.map(Math.abs), 1);
              const STEM = 90;
              const W = sorted.length * 56 + 40;
              return (
                <div className="db2-card db2-lollipop-card" style={{ gridColumn: "span 2" }}>
                  <div className="db2-card-header">
                    <span className="db2-card-label">Return Lollipop</span>
                    <span className="db2-card-sub">Each asset's return, up = positive, down = negative</span>
                  </div>
                  <div className="db2-lollipop-scroll">
                    <svg viewBox={`0 0 ${W} ${STEM * 2 + 60}`} width={W} height={STEM * 2 + 60} style={{ display: "block" }}>
                      <line x1={20} y1={STEM + 30} x2={W - 20} y2={STEM + 30} stroke="#e5e7eb" strokeWidth={1.5} />
                      {sorted.map((a, i) => {
                        const ret = rets[i];
                        const isPos = ret >= 0;
                        const x = 40 + i * 56;
                        const stemH = (Math.abs(ret) / absMax) * (STEM - 8);
                        const baseY = STEM + 30;
                        const tipY = isPos ? baseY - stemH : baseY + stemH;
                        const color = isPos ? "#4f46e5" : "#ef4444";
                        return (
                          <g key={i}>
                            <line x1={x} y1={baseY} x2={x} y2={tipY} stroke={color} strokeWidth={2} strokeOpacity={0.6} />
                            <circle cx={x} cy={tipY} r={7} fill={color} stroke="white" strokeWidth={2} />
                            <text x={x} y={tipY + (isPos ? -13 : 19)} textAnchor="middle" fontSize={8} fontWeight={800} fill={color} fontFamily="inherit">
                              {ret >= 0 ? "+" : ""}{ret.toFixed(1)}%
                            </text>
                            <text x={x} y={baseY + (isPos ? 14 : -6)} textAnchor="middle" fontSize={7.5} fill="#6b7280" fontFamily="inherit">
                              {(a.name || "").slice(0, 10)}
                            </text>
                          </g>
                        );
                      })}
                    </svg>
                  </div>
                </div>
              );
            })()}

            {/* ══ Year-in-Review Strip ══ */}
            {(() => {
              const curMonth = new Date().getMonth();
              const changes = yearReviewData.map((d, i) => {
                const prev = yearReviewData[i - 1];
                if (i === 0 || !prev?.value || !d.value) return null;
                return ((d.value - prev.value) / prev.value) * 100;
              });
              const validPcts = changes.filter(c => c !== null);
              const bestIdx  = changes.reduce((b, c, i) => c !== null && (b === null || c > changes[b]) ? i : b, null);
              const worstIdx = changes.reduce((w, c, i) => c !== null && (w === null || c < changes[w]) ? i : w, null);
              const posCount = validPcts.filter(c => c > 0).length;
              const negCount = validPcts.filter(c => c < 0).length;
              const firstVal = yearReviewData.find(d => d.value > 0)?.value;
              const curVal   = yearReviewData[curMonth]?.value;
              const totalPct = firstVal && curVal ? ((curVal - firstVal) / firstVal) * 100 : null;

              const cellStyle = (pct, hasData) => {
                if (!hasData) return { bg: "#f4f5f8", text: "#c4c4d0" };
                if (pct === null) return { bg: "#f0f0f5", text: "#c4c4d0" };
                if (pct > 10)  return { bg: "#059669", text: "#fff" };
                if (pct > 3)   return { bg: "#10b981", text: "#fff" };
                if (pct > 0)   return { bg: "#bbf7d0", text: "#166534" };
                if (pct > -3)  return { bg: "#fee2e2", text: "#991b1b" };
                if (pct > -10) return { bg: "#ef4444", text: "#fff" };
                return { bg: "#b91c1c", text: "#fff" };
              };

              return (
                <div className="db2-card db2-span-3 db2-year-card">
                  <div className="db2-card-header" style={{ marginBottom: 20 }}>
                    <div className="db2-card-header-left">
                      <div className="db2-card-label"><Clock size={11} /> {new Date().getFullYear()} Overview</div>
                      <h6 className="db2-card-title">Year in Review</h6>
                    </div>
                    <div className="db2-year-legend">
                      {[["#fee2e2","Loss"],["#f4f5f8","Flat"],["#bbf7d0","Gain"],["#059669","Strong"]].map(([bg, label]) => (
                        <div key={label} className="db2-year-legend-item">
                          <span style={{ width: 12, height: 12, borderRadius: 4, background: bg, border: "1px solid rgba(0,0,0,0.06)", display: "inline-block", flexShrink: 0 }} />
                          {label}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* 12 month cells */}
                  <div className="db2-year-grid">
                    {yearReviewData.map((d, i) => {
                      const pct = changes[i];
                      const isFuture = i > curMonth;
                      const isCurrent = i === curMonth;
                      const { bg, text } = cellStyle(pct, !isFuture && d.value > 0);
                      return (
                        <div key={i} className={`db2-year-month${isCurrent ? " db2-year-month-current" : ""}${isFuture ? " db2-year-month-future" : ""}`}>
                          <div className="db2-year-month-block" style={{ background: bg }}>
                            <span className="db2-year-month-pct" style={{ color: text }}>
                              {isFuture ? "—" : pct !== null ? `${pct >= 0 ? "+" : ""}${Math.round(pct)}%` : "—"}
                            </span>
                          </div>
                          <span className="db2-year-month-label">{d.day?.slice(0, 3)}</span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Summary chips */}
                  <div className="db2-year-summary">
                    <div className="db2-year-chip">
                      <span className="db2-year-chip-val" style={{ color: "#10b981" }}>{posCount}</span>
                      <span className="db2-year-chip-label">Gain months</span>
                    </div>
                    <div className="db2-year-chip">
                      <span className="db2-year-chip-val" style={{ color: "#ef4444" }}>{negCount}</span>
                      <span className="db2-year-chip-label">Loss months</span>
                    </div>
                    {bestIdx !== null && (
                      <div className="db2-year-chip">
                        <span className="db2-year-chip-val" style={{ color: "#059669" }}>+{Math.round(changes[bestIdx])}%</span>
                        <span className="db2-year-chip-label">Best · {yearReviewData[bestIdx]?.day?.slice(0,3)}</span>
                      </div>
                    )}
                    {worstIdx !== null && (
                      <div className="db2-year-chip">
                        <span className="db2-year-chip-val" style={{ color: "#ef4444" }}>{Math.round(changes[worstIdx])}%</span>
                        <span className="db2-year-chip-label">Worst · {yearReviewData[worstIdx]?.day?.slice(0,3)}</span>
                      </div>
                    )}
                    {totalPct !== null && (
                      <div className="db2-year-chip db2-year-chip-ytd">
                        <span className="db2-year-chip-val" style={{ color: totalPct >= 0 ? "#4f46e5" : "#ef4444" }}>
                          {totalPct >= 0 ? "+" : ""}{totalPct.toFixed(1)}%
                        </span>
                        <span className="db2-year-chip-label">YTD Return</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* ══ ROW 6: Waterfall | Top vs Bottom | Scatter ══ */}

            {/* Waterfall: Net Worth Breakdown */}
            {(() => {
              const totalA = summary.totalAssets?.value || 0;
              const totalL = summary.totalLiabilities?.value || 0;
              const nwVal  = summary.netWorth || 0;
              const wfData = [
                { name: "Assets",      base: 0,                  val: totalA, type: "asset" },
                { name: "Liabilities", base: Math.max(0, nwVal), val: totalL, type: "liab" },
                { name: "Net Worth",   base: 0,                  val: Math.max(0, nwVal), type: "nw" },
              ];
              return (
                <div className="db2-card db2-waterfall-card">
                  <div className="db2-card-header">
                    <span className="db2-card-label">Net Worth Breakdown</span>
                  </div>
                  <ResponsiveContainer width="100%" height={190}>
                    <BarChart data={wfData} margin={{ top: 12, right: 8, left: 8, bottom: 0 }} barCategoryGap="30%">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f5" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#9898b0" }} axisLine={false} tickLine={false} />
                      <YAxis hide />
                      <Tooltip content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const p = payload.find(x => x.dataKey === "val");
                        if (!p) return null;
                        return <div className="db2-tooltip">{cur} {formatLargeNumber(p.value, cur)}</div>;
                      }} />
                      <Bar dataKey="base" stackId="wf" fill="transparent" isAnimationActive={false} />
                      <Bar dataKey="val" stackId="wf" radius={[8, 8, 0, 0]}>
                        {wfData.map((d, i) => (
                          <Cell key={i} fill={d.type === "asset" ? "#4f46e5" : d.type === "liab" ? "#ef4444" : "#10b981"} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="db2-wf-legend">
                    {[
                      { label: "Assets",      color: "#4f46e5", value: totalA },
                      { label: "Liabilities", color: "#ef4444", value: totalL },
                      { label: "Net Worth",   color: "#10b981", value: nwVal },
                    ].map(({ label, color, value }) => (
                      <div key={label} className="db2-wf-legend-item">
                        <span className="db2-wf-dot" style={{ background: color }} />
                        <span className="db2-wf-legend-label">{label}</span>
                        <span className="db2-wf-legend-val">{cur} {formatLargeNumber(value, cur)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Top vs Bottom Performers */}
            {(() => {
              const tvbData = [
                ...topAssets.slice(0, 3).map(a => ({
                  name: (a.name || "Asset").slice(0, 18),
                  value: +(a.performanceMetrics?.totalReturnPercentage ?? 0),
                  type: "top",
                })),
                ...underAssets.slice(0, 3).map(a => ({
                  name: (a.name || "Asset").slice(0, 18),
                  value: +(a.performanceMetrics?.totalReturnPercentage ?? 0),
                  type: "under",
                })),
              ];
              if (!tvbData.length) return null;
              const absMax = Math.max(...tvbData.map(d => Math.abs(d.value || 0)), 1);
              return (
                <div className="db2-card db2-tvb-card">
                  <div className="db2-card-header">
                    <span className="db2-card-label">Top vs Bottom Performers</span>
                  </div>
                  <div className="db2-tvb-list">
                    {tvbData.map((d, i) => {
                      const w = (Math.abs(d.value || 0) / absMax) * 100;
                      const isTop = d.type === "top";
                      return (
                        <div key={i} className="db2-tvb-row">
                          <span className="db2-tvb-name">{d.name}</span>
                          <div className="db2-tvb-track">
                            <div className="db2-tvb-bar" style={{ width: `${w}%`, background: isTop ? "#10b981" : "#ef4444" }} />
                          </div>
                          <span className="db2-tvb-pct" style={{ color: isTop ? "#059669" : "#ef4444" }}>
                            {d.value >= 0 ? "+" : ""}{d.value.toFixed(1)}%
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Asset Performance Scatter */}
            {(() => {
              const allA = [...topAssets, ...underAssets];
              if (!allA.length) return null;
              const scatterData = allA.map(a => ({
                name: (a.name || "Asset").slice(0, 22),
                value: a.currentValue || a.value || 0,
                ret: +(a.performanceMetrics?.totalReturnPercentage ?? 0),
              }));
              return (
                <div className="db2-card db2-scatter-card">
                  <div className="db2-card-header">
                    <span className="db2-card-label">Asset Performance Map</span>
                    <div className="db2-scatter-legend">
                      <span><span className="db2-scatter-dot" style={{ background: "#4f46e5" }} />Positive</span>
                      <span><span className="db2-scatter-dot" style={{ background: "#ef4444" }} />Negative</span>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={200}>
                    <ScatterChart margin={{ top: 8, right: 12, left: 0, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f5" />
                      <XAxis type="number" dataKey="value" name="Value"
                        tick={{ fontSize: 10, fill: "#9898b0" }}
                        tickFormatter={v => formatLargeNumber(v, cur)}
                        axisLine={false} tickLine={false} />
                      <YAxis type="number" dataKey="ret" name="Return"
                        tick={{ fontSize: 10, fill: "#9898b0" }}
                        tickFormatter={v => `${v.toFixed(0)}%`}
                        axisLine={false} tickLine={false} />
                      <ReferenceLine y={0} stroke="#e5e7eb" strokeDasharray="4 4" />
                      <Tooltip content={<ScatterTooltip />} />
                      <Scatter data={scatterData} shape={(props) => {
                        const { cx, cy, payload } = props;
                        return <circle cx={cx} cy={cy} r={8} fill={payload.ret >= 0 ? "#4f46e5" : "#ef4444"} fillOpacity={0.82} stroke="#fff" strokeWidth={2} />;
                      }} />
                    </ScatterChart>
                  </ResponsiveContainer>
                </div>
              );
            })()}

            {/* ══ ROW 7: Financial Health Radar (full width) ══ */}
            {radarData.some(d => d.value > 0) && (
              <div className="db2-card db2-span-3 db2-radar-card">
                <div className="db2-card-header">
                  <span className="db2-card-label">Financial Health Radar</span>
                  <span className="db2-card-sub">Composite score across 5 dimensions</span>
                </div>
                <div className="db2-radar-inner">
                  <ResponsiveContainer width="100%" height={300}>
                    <RadarChart data={radarData} margin={{ top: 20, right: 60, bottom: 20, left: 60 }}>
                      <PolarGrid stroke="#e5e7eb" />
                      <PolarAngleAxis dataKey="axis" tick={{ fontSize: 12, fill: "#6b7280", fontWeight: 600 }} />
                      <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                      <Radar dataKey="value" stroke="#4f46e5" fill="#4f46e5" fillOpacity={0.18} strokeWidth={2}
                        dot={{ fill: "#4f46e5", r: 4, strokeWidth: 2, stroke: "#fff" }} />
                      <Tooltip content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        return (
                          <div className="db2-tooltip">
                            <strong>{payload[0]?.payload?.axis}</strong>
                            <div style={{ color: "#4f46e5", fontWeight: 700 }}>{payload[0]?.value} / 100</div>
                          </div>
                        );
                      }} />
                    </RadarChart>
                  </ResponsiveContainer>
                  <div className="db2-radar-scores">
                    {radarData.map(d => (
                      <div key={d.axis} className="db2-radar-score-row">
                        <span className="db2-radar-score-axis">{d.axis}</span>
                        <div className="db2-radar-score-bar">
                          <div className="db2-radar-score-fill" style={{
                            width: `${d.value}%`,
                            background: d.value >= 70 ? "#10b981" : d.value >= 40 ? "#f59e0b" : "#ef4444",
                          }} />
                        </div>
                        <span className="db2-radar-score-num" style={{
                          color: d.value >= 70 ? "#059669" : d.value >= 40 ? "#d97706" : "#ef4444",
                        }}>{d.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </Layout>
    </>
  );
};

export default Dashboard2;
